import React from 'react';
import { useFormikContext } from 'formik';
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from './contactPermissionsSection.module.css';
import {
  TContactAccessLevel,
  TContactPerson,
  TContactPermissions,
} from '../../../services/types/user.types';

// Every contact starts with full access and the admin narrows it — the backend's defaults.
export const FULL_CONTACT_PERMISSIONS: TContactPermissions = {
  calendar: 2,
  chat: 2,
  documents: 2,
  questionnairesView: true,
  questionnairesFill: true,
  exercisesView: true,
};

const LEVEL_OPTIONS: { value: TContactAccessLevel; labelKey: string }[] = [
  { value: 0, labelKey: 'CONTACT_ACCESS_NONE' },
  { value: 1, labelKey: 'CONTACT_ACCESS_VIEW' },
  { value: 2, labelKey: 'CONTACT_ACCESS_ACTION' },
];

// Areas with a none / view / action level. The hint says what "action" adds in that area.
const LEVELED_AREAS: { key: 'calendar' | 'chat' | 'documents'; labelKey: string; hintKey: string }[] = [
  { key: 'calendar', labelKey: 'CONTACT_AREA_CALENDAR', hintKey: 'CONTACT_AREA_CALENDAR_HINT' },
  { key: 'chat', labelKey: 'CONTACT_AREA_CHAT', hintKey: 'CONTACT_AREA_CHAT_HINT' },
  { key: 'documents', labelKey: 'CONTACT_AREA_DOCUMENTS', hintKey: 'CONTACT_AREA_DOCUMENTS_HINT' },
];

interface ContactPermissionsSectionProps {
  index: number;
}

/**
 * What this contact may see and do on the patient when they sign in to the patient app.
 * The server enforces it; this only edits contacts[index].permissions.
 */
function ContactPermissionsSection({ index }: ContactPermissionsSectionProps) {
  const { values, setFieldValue } = useFormikContext<{ contacts: TContactPerson[] }>();
  const permissions: TContactPermissions = {
    ...FULL_CONTACT_PERMISSIONS,
    ...(values.contacts?.[index]?.permissions ?? {}),
  };

  const update = (patch: Partial<TContactPermissions>) =>
    setFieldValue(`contacts.${index}.permissions`, { ...permissions, ...patch });

  return (
    <fieldset className={styles.permissions}>
      <legend className={styles.permissions__title}>{t('CONTACT_PERMISSIONS')}</legend>
      <p className={styles.permissions__intro}>{t('CONTACT_PERMISSIONS_HINT')}</p>

      <ul className={styles.permissions__list}>
        {LEVELED_AREAS.map((area) => (
          <li key={area.key} className={styles.permissions__row}>
            <div className={styles.permissions__area}>
              <span className={styles.permissions__areaName}>{t(area.labelKey)}</span>
              <span className={styles.permissions__areaHint}>{t(area.hintKey)}</span>
            </div>
            <div className={styles.permissions__options} role="radiogroup" aria-label={t(area.labelKey)}>
              {LEVEL_OPTIONS.map((option) => {
                const checked = permissions[area.key] === option.value;
                return (
                  <label
                    key={option.value}
                    className={`${styles.permissions__option} ${checked ? styles.permissions__option_active : ''}`}
                  >
                    <input
                      type="radio"
                      name={`contacts.${index}.permissions.${area.key}`}
                      checked={checked}
                      onChange={() => update({ [area.key]: option.value })}
                    />
                    {t(option.labelKey)}
                  </label>
                );
              })}
            </div>
          </li>
        ))}

        <li className={styles.permissions__row}>
          <div className={styles.permissions__area}>
            <span className={styles.permissions__areaName}>{t('CONTACT_AREA_QUESTIONNAIRES')}</span>
            <span className={styles.permissions__areaHint}>{t('CONTACT_AREA_QUESTIONNAIRES_HINT')}</span>
          </div>
          <div className={styles.permissions__options}>
            <label className={`${styles.permissions__option} ${permissions.questionnairesView ? styles.permissions__option_active : ''}`}>
              <input
                type="checkbox"
                checked={permissions.questionnairesView}
                onChange={(e) => update({ questionnairesView: e.target.checked })}
              />
              {t('CONTACT_QUESTIONNAIRES_VIEW')}
            </label>
            <label className={`${styles.permissions__option} ${permissions.questionnairesFill ? styles.permissions__option_active : ''}`}>
              <input
                type="checkbox"
                checked={permissions.questionnairesFill}
                onChange={(e) => update({ questionnairesFill: e.target.checked })}
              />
              {t('CONTACT_QUESTIONNAIRES_FILL')}
            </label>
          </div>
        </li>

        <li className={styles.permissions__row}>
          <div className={styles.permissions__area}>
            <span className={styles.permissions__areaName}>{t('CONTACT_AREA_EXERCISES')}</span>
          </div>
          <div className={styles.permissions__options}>
            <label className={`${styles.permissions__option} ${permissions.exercisesView ? styles.permissions__option_active : ''}`}>
              <input
                type="checkbox"
                checked={permissions.exercisesView}
                onChange={(e) => update({ exercisesView: e.target.checked })}
              />
              {t('CONTACT_ACCESS_VIEW')}
            </label>
          </div>
        </li>
      </ul>
    </fieldset>
  );
}

export default ContactPermissionsSection;
